import React from "react";
import "./UpdateFieldFailedAICard.scss";
import aiChatlogo from "../../../../../../assets/Images/aiChatlogo.png";
import { AILogo } from "../../../../../../assets/Icons/SparksSolid";
import { FieldUpdateIcon } from "../../../../../../assets/Icons/InputField";
import { StatusUpdateIcon } from "../../../../../../assets/Icons/StatusUpdateIcon";

export const UpdateFieldFailedAICard = ({
  message,
  onActionClick,
  disableButtons = false,
}) => {
  const now = new Date();
  const day = now.toLocaleDateString(undefined, {
    weekday: "long",
  });
  const time = now.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const recommendedActionsData = message.recommendedActions || {};
  // AXON-CHAT-VALIDATION-WARNINGS: preserve each backend warning as its own failed-card list item.
  const errorDetails = Array.isArray(message.errorDetails)
    ? message.errorDetails
        .map((errorItem) => String(errorItem || "").trim())
        .filter(Boolean)
    : [];

  const actionButtons = (() => {
    if (!message.showRecommendedActions || !recommendedActionsData) {
      return [];
    }

    if (Array.isArray(recommendedActionsData)) {
      return recommendedActionsData
        .map((item, index) => {
          const label =
            typeof item === "string"
              ? item
              : item?.label || item?.value || item?.name || "";

          return {
            id: item?.id || `action-${index}`,
            label: String(label || "").trim(),
            type: item?.type || "generic",
          };
        })
        .filter((item) => item.label.length > 0);
    }

    if (
      typeof recommendedActionsData !== "object" ||
      recommendedActionsData.isEmpty === true
    ) {
      return [];
    }

    return Object.entries(recommendedActionsData).flatMap(([key, values]) => {
      if (key === "isEmpty" || !Array.isArray(values)) {
        return [];
      }

      return values
        .map((value, index) => {
          const label = String(value || "").trim();
          if (!label) {
            return null;
          }

          return {
            id: `${key}-${index}`,
            label,
            type: key,
          };
        })
        .filter(Boolean);
    });
  })();

  const handleActionClick = (action) => {
    if (onActionClick) {
      onActionClick(action);
    }
  };

  return (
    <div className={`order-chat-bubble ${message.role}`}>
      <div className="order-ai-card update-field-failed-card">
        <div className="order-ai-card-header">
          <div className="order-ai-card-title-logo">
            <img
              src={aiChatlogo}
              alt="LOGO"
              aria-hidden="true"
              className="order-ai-logo"
              style={{ width: 32, height: 32 }}
            />
          </div>
          <div className="order-ai-card-title-Avatar">
            <span>{message.header?.title || "Axon AI"}</span>
            <div className="order-ai-card-time">{`${day} ${time}`}</div>
          </div>
        </div>

        <div className="order-ai-card-body">
          <div className="update-field-failed-container">
            <div className="failed-icon-wrapper">
              <span className="warning-icon">⚠️</span>
              <span className="failed-text">Failed to update the value.</span>
            </div>
            <div className="failed-message">
              <p>{message.errorMessage || "Invalid value provided."}</p>
              {errorDetails.length > 0 && (
                // AXON-CHAT-VALIDATION-WARNINGS: searchable warning list shown under the failed message.
                <ul className="failed-message-list">
                  {errorDetails.map((errorItem, index) => (
                    <li key={`${errorItem}-${index}`}>{errorItem}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="failed-instruction">
              <p>Please enter correct value.</p>
            </div>

            {actionButtons.length > 0 && (
              <div className="order-ai-actions">
                <div className="actions-title">
                  <span className="actions-title-icon">
                    <AILogo width="18" height="18" />
                  </span>
                  <span className="actions-title-text">Recommended Action</span>
                </div>
                <div className="actions-buttons-grouped">
                  {actionButtons.filter(
                    (btn) =>
                      btn.type === "fields_update" ||
                      btn.type === "fieldsUpdateRequired",
                  ).length > 0 && (
                    <div className="actions-category">
                      <span className="actions-category-label">
                        <FieldUpdateIcon
                          width={14}
                          height={14}
                          color="#a4a3b1"
                        />
                        Field Update
                      </span>
                      <div className="actions-buttons">
                        {actionButtons
                          .filter(
                            (btn) =>
                              btn.type === "fields_update" ||
                              btn.type === "fieldsUpdateRequired",
                          )
                          .map((action) => (
                            <button
                              key={action.id}
                              className={`order-ai-action-btn fields-update ${disableButtons ? "disabled" : ""}`.trim()}
                              onClick={() =>
                                !disableButtons && handleActionClick(action)
                              }
                              disabled={disableButtons}
                            >
                              {action.label}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {actionButtons.filter(
                    (btn) =>
                      btn.type === "state_update" || btn.type === "stateUpdate",
                  ).length > 0 && (
                    <div className="actions-category">
                      <span className="actions-category-label">
                        <StatusUpdateIcon
                          width={14}
                          height={14}
                          color="#a4a3b1"
                        />
                        Status Update
                      </span>
                      <div className="actions-buttons">
                        {actionButtons
                          .filter(
                            (btn) =>
                              btn.type === "state_update" ||
                              btn.type === "stateUpdate",
                          )
                          .map((action) => (
                            <button
                              key={action.id}
                              className={`order-ai-action-btn state-update ${disableButtons ? "disabled" : ""}`.trim()}
                              onClick={() =>
                                !disableButtons && handleActionClick(action)
                              }
                              disabled={disableButtons}
                            >
                              {action.label}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {actionButtons.filter(
                    (btn) =>
                      btn.type !== "fields_update" &&
                      btn.type !== "fieldsUpdateRequired" &&
                      btn.type !== "state_update" &&
                      btn.type !== "stateUpdate",
                  ).length > 0 && (
                    <div className="actions-category">
                      <div className="actions-buttons">
                        {actionButtons
                          .filter(
                            (btn) =>
                              btn.type !== "fields_update" &&
                              btn.type !== "fieldsUpdateRequired" &&
                              btn.type !== "state_update" &&
                              btn.type !== "stateUpdate",
                          )
                          .map((action) => (
                            <button
                              key={action.id}
                              className={`order-ai-action-btn ${disableButtons ? "disabled" : ""}`.trim()}
                              onClick={() =>
                                !disableButtons && handleActionClick(action)
                              }
                              disabled={disableButtons}
                            >
                              {action.label}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
